import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Typography, Spin, Breadcrumb, Tabs, message, Button, Row, Col, Rate, Slider, Checkbox, Select, Skeleton, Pagination, Popover } from "antd";
import { IconHome, IconChevronRight, IconTruck, IconBuildingStore, IconFilter, IconChevronDown } from "@tabler/icons-react";
import Navbar from "./Navbar";
import CategorySwiper from "../component/CategorySwiper";
import SafeImage from "../component/SafeImage";
import Breadcrumbs from "../component/Breadcrumbs";
import HomeSlider from "../component/HomeSlider";
import DealsSection from "../component/DealsSection";
import ShopProductRow from "../component/ShopProductRow";
import ProductCardSkeleton from "../component/skeletons/ProductCardSkeleton";
import ProductServ from "../service/ProductServ";
import ProductCard from "../component/ProductCard";
import { LocationContext } from "../LocationContext";
import { useCategory } from '../context/CategoryContext';
import { usePreference } from "../context/PreferenceContext";
import { capitalizeFirst, renderSafeText } from "../util/unitFormatter";
import { useDeliveryEstimate } from "../hooks/useDeliveryEstimate";
import EmptyState from "../component/EmptyState";
import "../css/categoryPage.css";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const renderName = (name) => {
    if (!name) return "";
    if (typeof name === 'string') return name;
    if (typeof name === 'object' && name.name) return name.name;
    return typeof name === 'object' ? (name.name || JSON.stringify(name)) : String(name);
};


const CategoryPage = () => {
    const navigate = useNavigate();
    const { categoryName } = useParams();
    const location = useLocation();
    const { latitude, longitude, radius } = useContext(LocationContext);
    const [loading, setLoading] = useState(true);
    const pageTopRef = useRef(null);
    const [categoryData, setCategoryData] = useState(null);
    const { categories: allCategories } = useCategory();
    const [selectedSubCategory, setSelectedSubCategory] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("sub") || "All";
    });
    const [expandedShops, setExpandedShops] = useState(false);
    const initialShopLimit = 6;
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 64;
    const [shopStatuses, setShopStatuses] = useState({});
    const [selectedShopId, setSelectedShopId] = useState(null);
    const [priceRange, setPriceRange] = useState([0, 2000]);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [sortBy, setSortBy] = useState("recommended");

    useEffect(() => {
        setSelectedShopId(null);
        setSelectedBrands([]);
        setCurrentPage(1);
        window.scrollTo(0, 0);
    }, [categoryName, selectedSubCategory]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedBrands, selectedShopId, priceRange, sortBy]);

    useEffect(() => {
        const handleShopStatusUpdate = (e) => {
            const data = e.detail;
            if (data?.shopId) {
                setShopStatuses(prev => ({
                    ...prev,
                    [data.shopId]: data.availability
                }));
            }
        };
        window.addEventListener('shopStatusUpdated', handleShopStatusUpdate);
        return () => window.removeEventListener('shopStatusUpdated', handleShopStatusUpdate);
    }, []);

    // Sync SubCategory from URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sub = params.get("sub");
        setSelectedSubCategory(sub || "All");
    }, [location.search, categoryName]);

    // Handle Direct Navigation to Subcategory and Auto-Parenting
    useEffect(() => {
        if (allCategories.length > 0) {
            const current = allCategories.find(c => renderName(c.name).toLowerCase() === decodeURIComponent(categoryName || "").toLowerCase());
            if (current && current.parentId) {
                // If it has a parent, it's a subcategory. Resolve parent and redirect.
                const parentId = current.parentId?._id || current.parentId;
                const parent = allCategories.find(c => (c._id || c.id) === parentId);
                if (parent) {
                    const parentName = renderName(parent.name);
                    const subName = renderName(current.name);
                    navigate(`/category/${encodeURIComponent(parentName)}?sub=${encodeURIComponent(subName)}`, { replace: true });
                }
            }
        }
    }, [categoryName, allCategories, navigate]);

    useEffect(() => {
        fetchCategoryProducts();
    }, [categoryName, latitude, longitude, radius]);

    const fetchCategoryProducts = async () => {
        try {
            setLoading(true);
            const params = {};
            if (latitude && longitude) {
                params.lat = latitude;
                params.lng = longitude;
                params.radius = radius;
            }
            const searchParams = new URLSearchParams(location.search);
            if (searchParams.get("search")) {
                params.search = searchParams.get("search");
            }
            const response = await ProductServ.getByCategoryGrouped(categoryName, params);
            if (response?.data) {
                setCategoryData(response.data);
            } else {
                setCategoryData({ category: categoryName, shops: [] });
            }
        } catch (error) {
            console.error("Error fetching category products:", error);
            message.error("Failed to load products");
            setCategoryData({ category: categoryName, shops: [] });
        } finally {
            setLoading(false);
            setTimeout(() => {
                window.scrollTo(0, 0);
            }, 50);
        }
    };

    // Get current category and its children IDs/Names
    const getValidCategoryNames = () => {
        if (allCategories.length === 0) return [categoryName];

        const currentCat = allCategories.find(c => renderName(c.name) === categoryName);
        if (!currentCat) return [categoryName];

        const children = allCategories.filter(c => (c.parentId?._id || c.parentId) === currentCat._id);
        return [categoryName, ...children.map(c => renderName(c.name))];
    };

    // Get unique sub-categories from products that are actually relevant to this page
    const subCategories = useMemo(() => {
        const currentCat = allCategories.find(c => renderName(c.name).toLowerCase() === decodeURIComponent(categoryName || "").toLowerCase());
        const children = currentCat ? allCategories.filter(c => (c.parentId?._id || c.parentId) === (currentCat._id || currentCat.id)) : [];

        const subCatsMap = new Map();
        children.forEach(c => subCatsMap.set(renderName(c.name), c.imageUrl));

        if (categoryData?.shops) {
            categoryData.shops.forEach(shop => {
                shop.products.forEach(product => {
                    const pCatName = renderName(product.category);
                    const pSubCatName = renderName(product.subCategory);

                    if (pCatName && pCatName.toLowerCase() !== decodeURIComponent(categoryName || "").toLowerCase()) {
                        if (!subCatsMap.has(pCatName)) subCatsMap.set(pCatName, product.imageUrl || null);
                    }
                    if (pSubCatName) {
                        if (!subCatsMap.has(pSubCatName)) subCatsMap.set(pSubCatName, product.imageUrl || null);
                    }
                });
            });
        }

        return [{ name: "All", imageUrl: currentCat?.imageUrl || null }, ...Array.from(subCatsMap.entries()).map(([name, imageUrl]) => ({ name, imageUrl }))];
    }, [categoryData, categoryName, allCategories]);

    // Filter shops based on selected sub-category
    const filteredShops = useMemo(() => {
        if (!categoryData?.shops) return [];

        const validNames = getValidCategoryNames();

        if (selectedSubCategory === "All") {
            // Trust the API: It already filters products by category & children.
            // Redundant string filtering can hide products with name mismatches
            return categoryData.shops
                .map(shop => ({
                    ...shop,
                    products: shop.products // Take all products from API
                }))
                .filter(shop => shop.products.length > 0);
        }

        // Specific sub-category filter (e.g. "Apple")
        const selectedSubCatId = allCategories.find(c => renderName(c.name).toLowerCase() === selectedSubCategory.toLowerCase())?._id;

        let shops = categoryData.shops
            .map(shop => ({
                ...shop,
                products: shop.products.filter(p => {
                    const pSubId = p.subCategoryId?._id || p.subCategoryId || "";
                    const pCatName = renderName(p.category).toLowerCase();
                    const pSubCatName = p.subCategory ? renderName(p.subCategory).toLowerCase() : "";

                    // 1. Matches Sub-category (by name or ID)
                    return (pCatName === selectedSubCategory.toLowerCase()) ||
                        (pSubCatName === selectedSubCategory.toLowerCase()) ||
                        (selectedSubCatId && String(pSubId) === String(selectedSubCatId));
                })
            }))
            .filter(shop => shop.products && shop.products.length > 0);

        return shops;
    }, [categoryData, selectedSubCategory, allCategories]);

    const rawProducts = useMemo(() => {
        const allProds = [];
        filteredShops.forEach(shop => {
            shop.products.forEach(p => {
                allProds.push({
                    ...p,
                    shopDetails: {
                        id: shop._id || shop.id,
                        name: renderName(shop.shopName || shop.name),
                        imageUrl: shop.imageUrl,
                        rating: shop.rating || 4.0,
                        description: shop.description
                    }
                });
            });
        });
        return allProds;
    }, [filteredShops]);

    const sidebarShops = useMemo(() => {
        return filteredShops.map(shop => ({
            id: shop._id || shop.id,
            name: renderName(shop.shopName || shop.name),
            imageUrl: shop.imageUrl,
            rating: shop.rating || 4.0,
            description: shop.description
        }));
    }, [filteredShops]);

    const categoryBrands = useMemo(() => {
        const brandMap = new Map();
        rawProducts.forEach(p => {
            const b = p.brandId || p.brandName;
            if (b) {
                const id = p.brandId?._id || p.brandId || b;
                const name = p.brandId?.name || p.brandName || b;
                brandMap.set(id.toString(), { id: id.toString(), name });
            }
        });
        return Array.from(brandMap.values());
    }, [rawProducts]);

    const maxPriceLimit = useMemo(() => {
        if (rawProducts.length === 0) return 2000;
        const max = Math.max(...rawProducts.map(p => p.price || 0));
        return max > 0 ? Math.ceil(max) : 2000;
    }, [rawProducts]);

    useEffect(() => {
        setPriceRange([0, maxPriceLimit]);
    }, [maxPriceLimit]);

    const filteredProducts = useMemo(() => {
        let items = [...rawProducts];

        if (selectedShopId) {
            items = items.filter(p => p.shopDetails?.id?.toString() === selectedShopId.toString());
        }

        items = items.filter(p => (p.price || 0) >= priceRange[0] && (p.price || 0) <= priceRange[1]);

        if (selectedBrands.length > 0) {
            items = items.filter(p => {
                const bId = p.brandId?._id || p.brandId || p.brandName;
                return bId && selectedBrands.includes(bId.toString());
            });
        }

        if (sortBy === 'price-asc') {
            items.sort((a, b) => (a.price || 0) - (b.price || 0));
        } else if (sortBy === 'price-desc') {
            items.sort((a, b) => (b.price || 0) - (a.price || 0));
        } else if (sortBy === 'rating') {
            items.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        }

        return items;
    }, [rawProducts, selectedShopId, priceRange, selectedBrands, sortBy]);

    const currentCatId = useMemo(() => {
        const found = allCategories.find(c => renderName(c.name).toLowerCase() === decodeURIComponent(categoryName || "").toLowerCase());
        return found?._id || found?.id;
    }, [allCategories, categoryName]);

    // Collect unique shopIds from visible shops for ONE shared delivery estimate
    const shopIdsForEstimate = useMemo(() => {
        const ids = filteredShops.map(s => (s._id || s.id)?.toString()).filter(Boolean);
        return [...new Set(ids)];
    }, [filteredShops]);

    const { formatETA } = useDeliveryEstimate(shopIdsForEstimate, latitude, longitude);

    return (
        <>
            <Navbar />

            {/* Category Pills Header */}
            <div ref={pageTopRef} className="category-pills-wrapper" style={{ background: '#ffffff', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'center' }}>
                <div className="category-pills-container" style={{ borderBottom: 'none', background: 'transparent' }}>
                    {/* Brand Filter Popover Pill Button (Placed BEFORE 'All') */}
                    {categoryBrands.length > 0 && (
                        <Popover
                            placement="bottomLeft"
                            trigger="click"
                            content={
                                <div style={{ minWidth: '200px', padding: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' }}>
                                        <span style={{ fontWeight: 700, color: '#0b1f4d', fontSize: '14px' }}>Filter by Brand</span>
                                        {selectedBrands.length > 0 && (
                                            <Button
                                                type="link"
                                                size="small"
                                                onClick={() => setSelectedBrands([])}
                                                style={{ padding: 0, height: 'auto', color: '#ff8110', fontSize: '12px' }}
                                            >
                                                Clear
                                            </Button>
                                        )}
                                    </div>
                                    <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                        <Checkbox.Group
                                            style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}
                                            value={selectedBrands}
                                            onChange={(checkedValues) => setSelectedBrands(checkedValues)}
                                        >
                                            {categoryBrands.map(brand => (
                                                <Checkbox key={brand.id} value={brand.id}>
                                                    {brand.name}
                                                </Checkbox>
                                            ))}
                                        </Checkbox.Group>
                                    </div>
                                </div>
                            }
                        >
                            <button
                                className={`category-pill-btn ${selectedBrands.length > 0 ? 'active' : ''}`}
                                style={{
                                    borderStyle: 'dashed',
                                    borderColor: selectedBrands.length > 0 ? '#ff8110' : '#cbd5e1',
                                    background: selectedBrands.length > 0 ? '#fff8f2' : '#ffffff',
                                    color: selectedBrands.length > 0 ? '#ff8110' : '#0b1f4d',
                                }}
                            >
                                <IconFilter size={15} />
                                <span>Brand {selectedBrands.length > 0 ? `(${selectedBrands.length})` : ''}</span>
                                <IconChevronDown size={14} />
                            </button>
                        </Popover>
                    )}

                    {subCategories.map(cat => {
                        const catName = cat.name;
                        const catImg = cat.imageUrl;
                        const isActive = selectedSubCategory === catName;
                        return (
                            <button
                                key={catName}
                                className={`category-pill-btn ${isActive ? 'active' : ''}`}
                                onClick={() => {
                                    setSelectedSubCategory(catName);
                                    const searchParams = new URLSearchParams(location.search);
                                    if (catName === "All") {
                                        searchParams.delete("sub");
                                    } else {
                                        searchParams.set("sub", catName);
                                    }
                                    const currentParams = new URLSearchParams(location.search);
                                    if (currentParams.get("search")) {
                                        searchParams.set("search", currentParams.get("search"));
                                    }
                                    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
                                }}
                            >
                                {catImg && (
                                    <img src={catImg} alt={catName} className="category-pill-img" onError={(e) => e.target.style.display = 'none'} />
                                )}
                                {renderSafeText(catName)}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Split Layout Container */}
            <div className="category-page-container">
                {loading ? (
                    <div className="category-split-layout">
                        {/* Skeleton Left Sidebar */}
                        <div className="category-left-sidebar" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div className="filter-sidebar-card" style={{ padding: '24px' }}>
                                {[1, 2, 3].map(i => (
                                    <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: i !== 3 ? '24px' : '0' }}>
                                        <Skeleton.Avatar active shape="square" size={80} />
                                        <div style={{ flex: 1 }}>
                                            <Skeleton active title={{ width: '80%' }} paragraph={{ rows: 2, width: ['100%', '60%'] }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Skeleton Right Content Pane */}
                        <div className="category-right-content">
                            <div className="category-products-header" style={{ borderBottom: 'none' }}>
                                <Skeleton.Button active style={{ width: '150px' }} size="small" />
                            </div>
                            <Row gutter={[12, 16]}>
                                {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                                    <Col xs={12} sm={12} md={8} lg={6} xl={6} key={i}>
                                        <ProductCardSkeleton />
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </div>
                ) : filteredShops.length === 0 ? (
                    <EmptyState
                        title={`No products available in your location`}
                        description={`We couldn't find any products in "${capitalizeFirst(selectedSubCategory === "All" ? categoryName : selectedSubCategory)}" near you. Try expanding your search radius or changing your location.`}
                        onRefresh={fetchCategoryProducts}
                    />
                ) : (
                    <div className="category-split-layout">
                        {/* Left Sidebar Filters */}
                        <div className="category-left-sidebar">

                            {/* Nearby Shops */}
                            {sidebarShops.length > 0 && (
                                <div className="filter-sidebar-card">
                                    <div className="sidebar-section-title" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', borderBottom: 'none', paddingBottom: '0' }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <span>Shops</span>
                                        </div>
                                    </div>
                                    <div style={{ paddingRight: '4px' }}>
                                        {sidebarShops.map(shop => {
                                            const shopId = shop.id;
                                            const sName = shop.name;
                                            const status = shopStatuses[shopId] || { isOpen: true };

                                            return (
                                                <div
                                                    key={shopId}
                                                    className="sidebar-shop-item"
                                                    onClick={() => {
                                                        if (shopId) {
                                                            navigate(`/products/${shopId}`);
                                                        }
                                                    }}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <SafeImage src={shop.imageUrl} alt={sName} className="sidebar-shop-img" placeholderText="Shop" />
                                                    <div className="sidebar-shop-info">
                                                        <h4 className="sidebar-shop-name" title={sName}>{sName}</h4>
                                                        <div className="sidebar-shop-desc">
                                                            {shop.description || "Pure cow milk and homemade dairy items...."}
                                                        </div>
                                                        <div className="sidebar-shop-badges">
                                                            <span className="badge-delivery">
                                                                <IconTruck size={14} style={{ marginRight: '4px' }} />
                                                                {formatETA(shopId?.toString(), "10 mins")}
                                                            </span>
                                                            <span className="badge-new">
                                                                ★ New
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                        </div>

                        {/* Right Content Pane - Products Grid */}
                        <div className="category-right-content">
                            <div className="category-products-header" style={{ borderBottom: 'none' }}>
                                <span className="category-products-title">
                                    {capitalizeFirst(selectedSubCategory === "All" ? categoryName : selectedSubCategory)}
                                </span>
                            </div>

                            {filteredProducts.length === 0 ? (
                                <EmptyState
                                    title={`No products match your filters`}
                                    description={`Try adjusting your price range, brands, or shop filters.`}
                                    onRefresh={() => {
                                        setSelectedShopId(null);
                                        setPriceRange([0, maxPriceLimit]);
                                        setSelectedBrands([]);
                                    }}
                                />
                            ) : (
                                <>
                                    <Row gutter={[12, 16]}>
                                        {filteredProducts.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((product, idx) => (
                                            <Col xs={12} sm={12} md={8} lg={6} xl={6} key={product._id || idx}>
                                                <ProductCard product={product} formatETA={formatETA} />
                                            </Col>
                                        ))}
                                    </Row>

                                    {filteredProducts.length > pageSize && (
                                        <div className="view-more-container pcu-margin-top-40" style={{ display: 'flex', justifyContent: 'center' }}>
                                            <Pagination
                                                current={currentPage}
                                                pageSize={pageSize}
                                                total={filteredProducts.length}
                                                onChange={(page) => {
                                                    setCurrentPage(page);
                                                    if (pageTopRef.current) {
                                                        pageTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                    } else {
                                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                                    }
                                                }}
                                                showSizeChanger={false}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default CategoryPage;
